/**
 * Pure colour math. No dependencies, no global state, no Math.random — every
 * function here is deterministic so derivePalette() is reproducible across the
 * onboarding wizard (browser), runtime services (Node), and scaffolded templates.
 *
 * All public hex strings are normalised to lowercase `#rrggbb`.
 */

export interface RGB {
  r: number; // 0..255
  g: number; // 0..255
  b: number; // 0..255
}

export interface HSL {
  h: number; // 0..360
  s: number; // 0..1
  l: number; // 0..1
}

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

/** Clamp to 0..255 and round to an integer channel value. */
const toChannel = (n: number): number => clamp(Math.round(n), 0, 255);

/**
 * Parse a hex colour into RGB. Accepts `#rgb`, `#rrggbb`, with or without the
 * leading `#`, any case. Throws on anything else — callers feed user input,
 * and a bad brand colour should fail loudly rather than render garbage.
 */
export function hexToRgb(hex: string): RGB {
  const raw = hex.trim().replace(/^#/, '');
  let normalized: string;
  if (/^[0-9a-fA-F]{3}$/.test(raw)) {
    normalized = raw
      .split('')
      .map((c) => c + c)
      .join('');
  } else if (/^[0-9a-fA-F]{6}$/.test(raw)) {
    normalized = raw;
  } else {
    throw new Error(`Invalid hex color: ${JSON.stringify(hex)}`);
  }
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

/** Serialise RGB to a normalised lowercase `#rrggbb` string. */
export function rgbToHex({ r, g, b }: RGB): string {
  const hx = (n: number) => toChannel(n).toString(16).padStart(2, '0');
  return `#${hx(r)}${hx(g)}${hx(b)}`;
}

export function rgbToHsl({ r, g, b }: RGB): HSL {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      default:
        h = (rn - gn) / d + 4;
        break;
    }
    h *= 60;
  }
  return { h, s, l };
}

export function hslToRgb({ h, s, l }: HSL): RGB {
  const hue = ((h % 360) + 360) % 360;
  const sat = clamp(s, 0, 1);
  const lum = clamp(l, 0, 1);
  if (sat === 0) {
    const v = toChannel(lum * 255);
    return { r: v, g: v, b: v };
  }
  const q = lum < 0.5 ? lum * (1 + sat) : lum + sat - lum * sat;
  const p = 2 * lum - q;
  const hk = hue / 360;
  const channel = (t: number): number => {
    let tc = t;
    if (tc < 0) tc += 1;
    if (tc > 1) tc -= 1;
    if (tc < 1 / 6) return p + (q - p) * 6 * tc;
    if (tc < 1 / 2) return q;
    if (tc < 2 / 3) return p + (q - p) * (2 / 3 - tc) * 6;
    return p;
  };
  return {
    r: toChannel(channel(hk + 1 / 3) * 255),
    g: toChannel(channel(hk) * 255),
    b: toChannel(channel(hk - 1 / 3) * 255),
  };
}

export function hexToHsl(hex: string): HSL {
  return rgbToHsl(hexToRgb(hex));
}

export function hslToHex(hsl: HSL): string {
  return rgbToHex(hslToRgb(hsl));
}

/** WCAG 2.x relative luminance of a colour, in 0..1. */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const lin = (c: number): number => {
    const cs = c / 255;
    return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two colours, in 1..21. Order-independent. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Lighten a colour by raising its HSL lightness by `amt` (0..1 of the full scale). */
export function lighten(hex: string, amt: number): string {
  const hsl = hexToHsl(hex);
  return hslToHex({ ...hsl, l: clamp(hsl.l + amt, 0, 1) });
}

/** Darken a colour by lowering its HSL lightness by `amt` (0..1 of the full scale). */
export function darken(hex: string, amt: number): string {
  return lighten(hex, -amt);
}

/**
 * Linearly interpolate between two colours in RGB space.
 * `amt` 0 returns `a`, `amt` 1 returns `b`.
 */
export function mix(a: string, b: string, amt: number): string {
  const t = clamp(amt, 0, 1);
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  return rgbToHex({
    r: ca.r + (cb.r - ca.r) * t,
    g: ca.g + (cb.g - ca.g) * t,
    b: ca.b + (cb.b - ca.b) * t,
  });
}

/** Adjust HSL saturation by `amt` (signed, 0..1 scale), clamped. */
export function saturate(hex: string, amt: number): string {
  const hsl = hexToHsl(hex);
  return hslToHex({ ...hsl, s: clamp(hsl.s + amt, 0, 1) });
}

export function desaturate(hex: string, amt: number): string {
  return saturate(hex, -amt);
}

/** Rotate hue by `deg` degrees. */
export function rotateHue(hex: string, deg: number): string {
  const hsl = hexToHsl(hex);
  return hslToHex({ ...hsl, h: hsl.h + deg });
}

const BLACK = '#000000';
const WHITE = '#ffffff';

/** Pick black or white — whichever has the higher contrast against `bg`. */
export function readableOn(bg: string): '#000000' | '#ffffff' {
  return contrastRatio(WHITE, bg) >= contrastRatio(BLACK, bg) ? WHITE : BLACK;
}

/**
 * Push `color` toward `target` until contrast(color, bg) >= `ratio`, in small
 * deterministic steps. Returns the original if the goal is already met or the
 * scale runs out (caller decides what to do then).
 */
export function adjustForContrast(
  color: string,
  bg: string,
  ratio: number,
  direction: 'lighten' | 'darken'
): string {
  let current = color;
  for (let i = 0; i < 100; i++) {
    if (contrastRatio(current, bg) >= ratio) return current;
    current = direction === 'lighten' ? lighten(current, 0.02) : darken(current, 0.02);
    const hsl = hexToHsl(current);
    if (direction === 'lighten' && hsl.l >= 1) return current;
    if (direction === 'darken' && hsl.l <= 0) return current;
  }
  return current;
}

/**
 * Make `fill` legible against BOTH black and white text by nudging it toward
 * mid-luminance only as far as needed: returns the fill plus the on-colour that
 * reaches `ratio`. Guarantees a readable result even for pure white/yellow
 * brands by darkening the fill until one of black/white clears the bar.
 */
export function legibleFill(fill: string, ratio: number): { fill: string; on: '#000000' | '#ffffff' } {
  let current = fill;
  for (let i = 0; i < 100; i++) {
    const on = readableOn(current);
    if (contrastRatio(on, current) >= ratio) return { fill: current, on };
    // Move the fill away from the nearest extreme to open up contrast headroom.
    const lum = relativeLuminance(current);
    current = lum > 0.5 ? darken(current, 0.02) : lighten(current, 0.02);
    const hsl = hexToHsl(current);
    if (hsl.l <= 0 || hsl.l >= 1) break;
  }
  return { fill: current, on: readableOn(current) };
}
